import React from 'react';
import axios from 'helpers/axios';
import _ from 'lodash';

import {
  Form,
  Container,
  Title,
  TextInput,
  Button,
  Label,
  ToggleSwitch,
  MultiTextInput,
  Separator,
} from 'ui/components/styled';
import { useToasts, useCompany } from 'hooks';
import { ValidationPattern } from 'helpers/form';
import { defaultJobSettings } from 'providers/company';

export const EditCompany = ({ company, onClose, onSuccess }) => {
  const [state, setState] = React.useState({
    name: company?.name,
    email: company?.email.split('@')[0],
    settings: {
      hoursUntilWarning: company?.settings?.hoursUntilWarning || '',
      emailCapacity: company?.settings?.emailCapacity || '',
      notifications: {
        enabled: company?.settings?.notifications?.enabled || false,
        receivers: company?.settings?.notifications?.receivers || [],
      },
      reports: {
        receivers: company?.settings?.reports?.receivers || [],
      },
    },
  });
  const [preventSubmit, setPreventSubmit] = React.useState(false);
  const { errorToast, successToast, removeAllToasts } = useToasts();
  const { refreshCompanies } = useCompany();
  const emailSuffix = React.useMemo(() => `@${company?.email.split('@')[1]}`, [company]);
  const notificationsReceiversRef = React.useRef();

  const handleChange = (e) => {
    const { name, type } = e.target;
    const value = type === 'checkbox' ? e.target.checked : e.target.value;
    _.set(state, name, value);
    setState({ ...state });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setPreventSubmit(true);
      const response = await axios.put(`/companies/${company?.id}`, {
        ...state,
        email: `${state.email}${emailSuffix}`,
        settings: {
          ...state.settings,
          hoursUntilWarning:
            parseInt(state.settings.hoursUntilWarning) || defaultJobSettings.hoursUntilWarning,
          emailCapacity: parseInt(state.settings.emailCapacity) || defaultJobSettings.emailCapacity,
        },
      });
      if (response.status === 200) {
        removeAllToasts();
        successToast('Company updated successfully!');
        refreshCompanies();
        if (onClose) {
          onClose();
        }
        if (onSuccess) {
          onSuccess();
        }
        return;
      }
    } catch (error) {
      errorToast({ error, default: 'Error: Edit Company.' });
    }
    setPreventSubmit(false);
  };

  return (
    <Form onSubmit={handleSubmit}>
      <Container mb="xs">
        <Title variant="darkGrey" fontWeight="500">
          Edit company
        </Title>
      </Container>
      <Container mb="s">
        <Label htmlFor="name" mb="xs">
          Company name
        </Label>
        <TextInput
          id="name"
          name="name"
          variant="minimal"
          placeholder="name"
          value={state.name}
          onChange={handleChange}
          required
          autoFocus
        />
      </Container>
      <Container mb="xxs">
        <Label htmlFor="email" mb="xs">
          Monitors email
        </Label>
        <Container
          flexDirection="row"
          bg="#F5F5F5"
          height="auto"
          alignItems="center"
          borderRadius="8px"
          overflow="hidden"
          boxShadow="0px 1px 6px rgb(0 0 0 / 16%)"
        >
          <TextInput
            id="email"
            name="email"
            //type="email"
            variant="minimal"
            placeholder="Email"
            value={state.email}
            onChange={handleChange}
            width="auto"
            flexGrow="1"
            boxShadow="none"
            borderRadius="0"
            required
          />
          <Container
            justifyContent="center"
            minWidth="200px"
            width="auto"
            userSelect="none"
            px="xs"
          >
            {emailSuffix}
          </Container>
        </Container>
      </Container>
      <Separator my="m" />
      <Container mb="s">
        <Label htmlFor="hours-until-warning" mb="xs">
          Hours until warning
        </Label>
        <TextInput
          id="hours-until-warning"
          name="settings.hoursUntilWarning"
          type="number"
          min="1"
          placeholder="Hours until warning"
          variant="minimal"
          value={state.settings.hoursUntilWarning}
          onChange={handleChange}
          required
        />
      </Container>
      <Container mb="s">
        <Label htmlFor="email-capacity" mb="xs">
          Email capacity
        </Label>
        <TextInput
          name="settings.emailCapacity"
          type="number"
          min="1"
          placeholder="Email capacity"
          variant="minimal"
          value={state.settings.emailCapacity}
          onChange={handleChange}
          required
        />
      </Container>
      <Container mb="s">
        <Label htmlFor="notifications" mb="xs">
          Notifications to
        </Label>
        <Container flexDirection="row" alignItems="center">
          <ToggleSwitch
            id="notifications"
            name="settings.notifications.enabled"
            testId="notifications-toggle"
            checked={state.settings.notifications?.enabled}
            onChange={(event) => {
              handleChange(event);
              if (event.target.checked) {
                notificationsReceiversRef.current.focus();
              }
            }}
            mr="s"
          />
          <MultiTextInput
            ref={notificationsReceiversRef}
            variant="minimal"
            placeholder="Email receivers"
            value={state.settings.notifications?.receivers}
            onChange={(values) =>
              handleChange({
                target: {
                  name: 'settings.notifications.receivers',
                  value: values,
                },
              })
            }
            validateValues={(value) => {
              const regex = new RegExp(ValidationPattern.email, 'i');
              const isValid = regex.test(value);
              if (!isValid) {
                errorToast('Please enter a valid email address.');
              }
              return isValid;
            }}
            disabled={!state.settings.notifications?.enabled}
            required
          />
        </Container>
      </Container>
      {/*
      <Container>
        <Label htmlFor="reports" mb="xs">
          Reports to
        </Label>
        <MultiTextInput
          variant="minimal"
          placeholder="Reports receivers"
          value={state.settings.reports?.receivers}
          onChange={(values) =>
            handleChange({
              target: {
                name: 'settings.reports.receivers',
                value: values,
              },
            })
          }
          validateValues={(value) => {
            const regex = new RegExp(ValidationPattern.email, 'i');
            const isValid = regex.test(value);
            if (!isValid) {
              errorToast('Please enter a valid email address.');
            }
            return isValid;
          }}
          mb="s"
        />
      </Container>
      */}
      <Container mb="m">
        <Label variant="legend">These are the default settings for each new monitor created.</Label>
      </Container>
      <Container flexDirection="row" justifyContent="flex-end" mt="xxs">
        <Button
          variant="contrast-primary"
          width="30%"
          disabled={preventSubmit}
          onClick={onClose}
          boxShadow="none"
          mr="s"
        >
          Close
        </Button>
        <Button type="submit" width="30%" disabled={preventSubmit}>
          Save
        </Button>
      </Container>
    </Form>
  );
};
